import { useNavigate } from 'react-router-dom';
import type { FrameworkReadiness } from '@/api/types/compliance-dashboard.types';
import { ChartSkeletonLoader } from './ChartSkeletonLoader';

// Slice 2 (BI): one row per framework — readiness, evidence coverage, unaddressed and open
// exceptions at a glance. Real state only: a 0% metric greys out and draws no bar; zero exceptions
// render as an em-dash rather than a bar.
interface FrameworkScorecardProps {
  data: FrameworkReadiness[];
  isLoading: boolean;
}

const CYAN = '#4FA8E8';
const GREEN = '#4A9E78';
const FAINTEST = '#5C7189';

function MetricCell({ pct, color }: { pct: number; color: string }) {
  const on = pct > 0;
  const hex = on ? color : FAINTEST;
  return (
    <span className="flex flex-[0_0_140px] flex-col gap-1">
      <span className="text-[12px] font-semibold tabular-nums" style={{ color: hex }}>
        {pct}%
      </span>
      <span className="flex h-[4px] overflow-hidden rounded-[3px] bg-[#0D1626]">
        <span className="block h-full" style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: hex }} />
      </span>
    </span>
  );
}

export function FrameworkScorecard({ data, isLoading }: FrameworkScorecardProps) {
  const navigate = useNavigate();
  // A row opens that framework's control tracker (framework_id already on the row — no api).
  const openFramework = (id: string) => navigate(`/compliance/controls/${id}`);

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-xl border border-cert-info-border bg-bg-surface">
        <ChartSkeletonLoader />
      </div>
    );
  }

  return (
    <section aria-label="Framework scorecard" data-testid="framework-scorecard">
      <div className="mb-3 flex flex-wrap items-center gap-[10px]">
        <span className="h-[15px] w-[3px] rounded-[2px]" style={{ background: CYAN }} />
        <span className="text-[15px] font-semibold tracking-[-0.01em] text-text-primary">Framework scorecard</span>
        <span className="ml-auto text-[11.5px] text-[#7A8CA3]">
          One row per framework — readiness, evidence and open work at a glance
        </span>
      </div>
      <div className="overflow-hidden rounded-xl border border-cert-info-border bg-bg-surface">
        <div className="overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
          <div className="min-w-[760px]">
            <div className="flex items-center gap-3 border-b border-border-subtle bg-[#0D1626] px-4 py-[11px] text-[10px] font-bold uppercase tracking-[0.1em] text-text-secondary">
              <span className="flex flex-[1_1_240px]">Framework</span>
              <span className="flex-[0_0_140px]">Readiness</span>
              <span className="flex-[0_0_140px]">Evidence</span>
              <span className="flex-[0_0_110px] text-right">Unaddressed</span>
              <span className="flex-[0_0_90px] text-right">Exceptions</span>
            </div>
            {data.map((fw, i) => (
              <div
                key={fw.framework_id}
                role="button"
                tabIndex={0}
                onClick={() => openFramework(fw.framework_id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openFramework(fw.framework_id);
                  }
                }}
                className={`flex cursor-pointer items-center gap-3 border-b border-border-subtle px-4 py-[11px] transition-colors last:border-b-0 hover:bg-bg-surface-2 focus-visible:bg-bg-surface-2 focus:outline-none ${
                  i % 2 === 0 ? 'bg-bg-surface' : 'bg-[#121F33]'
                }`}
                data-testid="scorecard-row"
              >
                <span className="flex min-w-[190px] flex-[1_1_240px] flex-col gap-[2px]">
                  <span className="truncate text-[13px] font-semibold text-text-primary">{fw.display_name}</span>
                  <span className="text-[10.5px] text-[#6E829B]">{fw.total_controls} controls in scope</span>
                </span>
                <MetricCell pct={fw.readiness_pct} color={CYAN} />
                <MetricCell pct={fw.evidence_coverage_pct} color={GREEN} />
                <span className="flex-[0_0_110px] text-right text-[12.5px] font-semibold tabular-nums text-text-muted">
                  {fw.unaddressed}
                </span>
                <span className="flex-[0_0_90px] text-right text-[12.5px] tabular-nums">
                  {fw.open_exceptions_count > 0 ? (
                    <span className="font-semibold text-accent-amber">{fw.open_exceptions_count}</span>
                  ) : (
                    <span style={{ color: '#4E627C' }}>—</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-border-subtle bg-[#0D1626] px-4 py-[10px] text-[11.5px] leading-[1.5] text-text-secondary">
          Readiness, evidence, and open work by framework.
        </div>
      </div>
    </section>
  );
}
